import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileOpen as FileOpenIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import AuditTrailComponent from './AuditTrailComponent';
import moment from 'moment';

/**
 * TabPanel Component
 * Wrapper for tab content
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-tabpanel-${index}`}
      aria-labelledby={`transaction-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * TransactionDetailModal Component
 * Displays transaction details with audit trail tab
 * Phase 8.2 Implementation
 */
const TransactionDetailModal = ({
  open = false,
  transaction = null,
  onClose = () => {},
}) => {
  const [tabValue, setTabValue] = useState(0);

  if (!transaction) return null;

  const {
    id = '',
    transactionId = '',
    referenceNumber = '',
    status = 'UNKNOWN',
    amount = 0,
    paymentMethod = 'N/A',
    bankName = 'N/A',
    createDateTime = new Date().toISOString(),
  } = transaction;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value || 0);
  };

  // Get status color
  const getStatusColor = (stat) => {
    const colors = {
      'INITIATED': 'warning',
      'PROCESSING': 'info',
      'VERIFICATION_PENDING': 'info',
      'COMPLETED': 'success',
      'PAID': 'success',
      'FAILED': 'error',
      'REJECTED': 'error',
    };
    return colors[stat] || 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.3rem',
          bgcolor: '#0E6B6B',
          color: 'white',
          mb: 0,
          py: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FileOpenIcon />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Transaction Details - Audit Validate
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Ref: {referenceNumber || id.substring(0, 8)}
            </Typography>
          </Box>
        </Stack>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <CloseIcon />
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          aria-label="transaction details tabs"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 500,
              minHeight: 56,
            },
            '& .Mui-selected': {
              color: '#0E6B6B',
            },
          }}
        >
          <Tab
            label="Overview"
            icon={<FileOpenIcon sx={{ mr: 1 }} />}
            iconPosition="start"
            id="transaction-tab-0"
            aria-controls="transaction-tabpanel-0"
          />
          <Tab
            label="Audit Trail"
            icon={<HistoryIcon sx={{ mr: 1 }} />}
            iconPosition="start"
            id="transaction-tab-1"
            aria-controls="transaction-tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Content */}
      <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Tab 1: Overview */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            {/* Status Badge */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Status
              </Typography>
              <Chip
                label={status}
                color={getStatusColor(status)}
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Transaction Details Grid */}
            <Grid container spacing={3}>
              {/* Transaction ID */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Transaction ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                  }}
                >
                  {transactionId || id}
                </Typography>
              </Grid>

              {/* Reference Number */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Reference Number
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {referenceNumber || 'N/A'}
                </Typography>
              </Grid>

              {/* Amount */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Amount
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, fontWeight: 600, color: '#0E6B6B' }}
                >
                  {formatCurrency(amount)}
                </Typography>
              </Grid>

              {/* Date */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Date & Time
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {moment(createDateTime).format('MMM DD, YYYY HH:mm:ss')}
                </Typography>
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Payment Method
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {paymentMethod}
                </Typography>
              </Grid>

              {/* Bank Name */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Destination Bank
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {bankName || 'N/A'}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Additional Info */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                For comprehensive transaction history and audit trail details, please visit the Audit Trail tab.
              </Typography>
            </Alert>
          </Box>
        </TabPanel>

        {/* Tab 2: Audit Trail */}
        <TabPanel value={tabValue} index={1}>
          <AuditTrailComponent transactionId={transactionId || id} />
        </TabPanel>
      </DialogContent>

      {/* Dialog Footer */}
      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#0E6B6B',
            '&:hover': {
              bgcolor: '#0A5151',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailModal;
